import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { COUNTRY_CODES, countryName } from "../data/countries";
import rawCatalog from "../data/tool-catalog.json";
import type { Tool } from "../types";
import type { AccessConfig } from "../types";
import { navigate } from "../utils/navigation";

type AuditRecord = { id: number; action: string; country: string; details: string; created_at: string };
type PageVisit = { id: number; page_path: string; ip_address: string; country: string; device_type: string; operating_system: string; browser: string; browser_id: string; user_agent: string; visited_at: string; duration_seconds: number; visit_token: string | null; ended_at: string | null; username: string; visitor_timezone: string; location_detail: string; manufacturer: string; device_model: string; platform_version: string; browser_version: string; cpu_architecture: string; cpu_bitness: string; cpu_cores: string; device_memory_gb: string; screen_resolution: string };
type VisitEvent = { id: number; component: string; component_type: string; action: string; value: string; occurred_at: string };
type PaymentMethod = { id: string; name: string; group: string; integration: "ready" | "not_integrated"; entities: string; coverage: string; requirements: string; configured: boolean; status: string; credentialStatus: string; enabled: boolean };
type PaymentProduct = { id: string; name: string; description: string; currency: string; amount_minor: number; active: number };
type PaymentOrder = { id: string; product_name: string; amount_minor: number; currency: string; status: string; provider: string; customer_email: string | null; username: string; created_at: string; paid_at: string | null };
type MonetizationItem = { id: string; kind: "ad" | "cps" | "sponsor" | "referral" | "donation"; provider: string; title: string; description: string; target_url: string; image_url: string; placement: "home" | "tool" | "both"; disclosure: string; active: number; income_minor: number | null; click_count: number };
type RevenueMethod = { id: string; name: string; type: "用户直接支付" | "第三方支付"; incomeMinor: number | null; incomeStatus: string; paidOrders?: number; active?: boolean; category?: MonetizationItem["kind"] };
type MonetizationConfigSource = { id: string; name: string; category: string; revenueType: "direct" | "third_party"; integration: "ready" | "not_integrated"; configured: boolean; updatedAt: string; custom?: boolean };
type FeatureAccessRule = { id: string; page_id: string; scope: "page" | "component"; component_selector: string; label: string; paid: number; product_id: string | null; product_name: string | null; amount_minor: number | null; product_active: number | null; updated_at: string };
const tools = rawCatalog as Tool[];
const COMPONENT_OPTIONS = [
  [".form-grid", "输入参数组"], [".food-grid", "食材输入组"], [".assessment-list", "测评题目列表"], [".assessment-row", "单个测评题"],
  [".score-buttons", "评分选项"], [".result-grid", "计算结果组"], [".result-box", "结果卡片"], [".data-list", "数据结果列表"],
  [".quiz-options", "问卷选项"], [".quiz-controls", "问卷操作按钮"], [".guide-section", "指南内容分组"], [".check-list", "清单"],
  [".file-drop", "文件上传区域"], [".print-sheet", "打印预览"], [".field", "输入字段"], ["h2", "功能标题"],
] as const;

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds} 秒`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes < 60) return remainder ? `${minutes} 分 ${remainder} 秒` : `${minutes} 分`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours} 小时 ${remainingMinutes} 分` : `${hours} 小时`;
}

function visitClientType(pagePath: string): "平台端" | "用户端" {
  return pagePath === "/admin" || pagePath.startsWith("/admin/") ? "平台端" : "用户端";
}

function paymentStatusName(status: string): string {
  return ({ pending: "待支付", paid: "已支付", failed: "支付失败", expired: "已过期", refunded: "已退款" } as Record<string, string>)[status] || status;
}

function formatVisitTime(value: string, timeZone: string): string {
  const date = new Date(`${value.replace(" ", "T")}Z`);
  try {
    return new Intl.DateTimeFormat("zh-CN", { timeZone, dateStyle: "medium", timeStyle: "medium" }).format(date);
  } catch {
    return "—";
  }
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: { ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers },
  });
  const data = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(data.error || "请求失败");
  return data;
}

export function AdminPage() {
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<AccessConfig>({ mode: "allow_all", allowedCountries: [], blockedCountries: [], updatedAt: "" });
  const [allowQuery, setAllowQuery] = useState("");
  const [blockQuery, setBlockQuery] = useState("");
  const [audits, setAudits] = useState<AuditRecord[]>([]);
  const [visits, setVisits] = useState<PageVisit[]>([]);
  const [visitsTotal, setVisitsTotal] = useState(0);
  const [visitsOffset, setVisitsOffset] = useState(0);
  const [selectedVisit, setSelectedVisit] = useState<PageVisit | null>(null);
  const [visitEvents, setVisitEvents] = useState<VisitEvent[]>([]);
  const [loadingVisitEvents, setLoadingVisitEvents] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [paymentProducts, setPaymentProducts] = useState<PaymentProduct[]>([]);
  const [paymentOrders, setPaymentOrders] = useState<PaymentOrder[]>([]);
  const [monetizationItems, setMonetizationItems] = useState<MonetizationItem[]>([]);
  const [revenueMethods, setRevenueMethods] = useState<RevenueMethod[]>([]);
  const [providerConfigs, setProviderConfigs] = useState<MonetizationConfigSource[]>([]);
  const [featureAccessRules, setFeatureAccessRules] = useState<FeatureAccessRule[]>([]);
  const [featurePageId, setFeaturePageId] = useState(tools[0]?.id || "");
  const [featureScope, setFeatureScope] = useState<"page" | "component">("page");
  const [featureSelector, setFeatureSelector] = useState<string>(COMPONENT_OPTIONS[0][0]);
  const [featureLabel, setFeatureLabel] = useState(tools[0]?.title || "");
  const [featurePaid, setFeaturePaid] = useState(false);
  const [featureProductId, setFeatureProductId] = useState("");
  const [featureSearch, setFeatureSearch] = useState("");
  const [featureSaving, setFeatureSaving] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState("alipay");
  const [providerConfigJson, setProviderConfigJson] = useState("");
  const [alipayAppId, setAlipayAppId] = useState("");
  const [alipayPrivateKey, setAlipayPrivateKey] = useState("");
  const [alipayPublicKey, setAlipayPublicKey] = useState("");
  const [customProviderName, setCustomProviderName] = useState("");
  const [customProviderCategory, setCustomProviderCategory] = useState("其他");
  const [customProviderRevenueType, setCustomProviderRevenueType] = useState<"direct" | "third_party">("third_party");
  const [configSaving, setConfigSaving] = useState(false);
  const [monetizationSaving, setMonetizationSaving] = useState(false);
  const [editingRevenueId, setEditingRevenueId] = useState("");
  const [newRevenueKind, setNewRevenueKind] = useState<MonetizationItem["kind"]>("cps");
  const [newRevenueProvider, setNewRevenueProvider] = useState("");
  const [newRevenueTitle, setNewRevenueTitle] = useState("");
  const [newRevenueDescription, setNewRevenueDescription] = useState("");
  const [newRevenueUrl, setNewRevenueUrl] = useState("");
  const [newRevenueImage, setNewRevenueImage] = useState("");
  const [newRevenuePlacement, setNewRevenuePlacement] = useState<MonetizationItem["placement"]>("home");
  const [newRevenueDisclosure, setNewRevenueDisclosure] = useState("推广链接，成交后可能获得佣金。");
  const [newRevenueIncome, setNewRevenueIncome] = useState("");
  const [newProductName, setNewProductName] = useState("");
  const [newProductDescription, setNewProductDescription] = useState("");
  const [newProductPrice, setNewProductPrice] = useState("");
  const [paymentSaving, setPaymentSaving] = useState(false);
  const visitsTableWrap = useRef<HTMLDivElement>(null);
  const topVisitsScrollbar = useRef<HTMLDivElement>(null);
  const bottomVisitsScrollbar = useRef<HTMLDivElement>(null);
  const topVisitsScrollbarSpace = useRef<HTMLDivElement>(null);
  const bottomVisitsScrollbarSpace = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const table = visitsTableWrap.current;
    const topBar = topVisitsScrollbar.current;
    const bottomBar = bottomVisitsScrollbar.current;
    const topSpace = topVisitsScrollbarSpace.current;
    const bottomSpace = bottomVisitsScrollbarSpace.current;
    if (!table || !topBar || !bottomBar || !topSpace || !bottomSpace) return;

    let synchronizing = false;
    let releaseSync = 0;
    const syncWidths = () => {
      topSpace.style.width = `${Math.max(table.scrollWidth, table.clientWidth)}px`;
      bottomSpace.style.width = `${Math.max(table.scrollWidth, table.clientWidth)}px`;
      const bounds = table.getBoundingClientRect();
      bottomBar.style.left = `${bounds.left}px`;
      bottomBar.style.width = `${bounds.width}px`;
      topBar.scrollLeft = table.scrollLeft;
      bottomBar.scrollLeft = table.scrollLeft;
    };
    const syncFrom = (source: HTMLElement) => {
      if (synchronizing || (table.scrollLeft === source.scrollLeft && topBar.scrollLeft === source.scrollLeft && bottomBar.scrollLeft === source.scrollLeft)) return;
      synchronizing = true;
      table.scrollLeft = source.scrollLeft;
      topBar.scrollLeft = source.scrollLeft;
      bottomBar.scrollLeft = source.scrollLeft;
      window.clearTimeout(releaseSync);
      releaseSync = window.setTimeout(() => { synchronizing = false; }, 0);
    };
    const onTableScroll = () => syncFrom(table);
    const onTopScroll = () => syncFrom(topBar);
    const onBottomScroll = () => syncFrom(bottomBar);
    topBar.addEventListener("scroll", onTopScroll, { passive: true });
    bottomBar.addEventListener("scroll", onBottomScroll, { passive: true });
    table.addEventListener("scroll", onTableScroll, { passive: true });
    window.addEventListener("resize", syncWidths);
    const observer = new ResizeObserver(syncWidths);
    observer.observe(table);
    if (table.firstElementChild) observer.observe(table.firstElementChild);
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      bottomBar.hidden = !entry.isIntersecting || entry.intersectionRect.height === 0;
    });
    visibilityObserver.observe(table);
    syncWidths();
    return () => {
      topBar.removeEventListener("scroll", onTopScroll);
      bottomBar.removeEventListener("scroll", onBottomScroll);
      table.removeEventListener("scroll", onTableScroll);
      window.removeEventListener("resize", syncWidths);
      observer.disconnect();
      visibilityObserver.disconnect();
      window.clearTimeout(releaseSync);
    };
  }, [visits.length]);

  const loadVisits = async (offset: number) => {
    const data = await api<{ records: PageVisit[]; total: number }>(`/api/admin/visits?limit=50&offset=${offset}`);
    setVisits(data.records);
    setVisitsTotal(data.total);
    setVisitsOffset(offset);
  };

  const openVisitDetails = async (visit: PageVisit) => {
    setSelectedVisit(visit);
    setVisitEvents([]);
    setLoadingVisitEvents(true);
    try {
      const data = await api<{ events: VisitEvent[] }>(`/api/admin/visit-events?visitId=${visit.id}`);
      setVisitEvents(data.events);
    } catch (detailError) {
      setError(detailError instanceof Error ? detailError.message : "访问明细加载失败");
    } finally {
      setLoadingVisitEvents(false);
    }
  };

  const loadAdmin = async () => {
    try {
      await api("/api/admin/session");
      const [nextConfig, auditData, visitData, methodData, productData, orderData, monetizationData, revenueData, providerConfigData, featureAccessData] = await Promise.all([
        api<AccessConfig>("/api/admin/config"),
        api<{ records: AuditRecord[] }>("/api/admin/audit"),
        api<{ records: PageVisit[]; total: number }>("/api/admin/visits?limit=50&offset=0"),
        api<{ methods: PaymentMethod[] }>("/api/admin/payment-methods"),
        api<{ products: PaymentProduct[] }>("/api/admin/payment-products"),
        api<{ orders: PaymentOrder[] }>("/api/admin/payment-orders"),
        api<{ items: MonetizationItem[] }>("/api/admin/monetization-items"),
        api<{ methods: RevenueMethod[] }>("/api/admin/revenue-methods"),
        api<{ sources: MonetizationConfigSource[] }>("/api/admin/monetization-configs"),
        api<{ rules: FeatureAccessRule[] }>("/api/admin/feature-access"),
      ]);
      setConfig(nextConfig);
      setAudits(auditData.records);
      setVisits(visitData.records);
      setVisitsTotal(visitData.total);
      setVisitsOffset(0);
      setPaymentMethods(methodData.methods);
      setPaymentProducts(productData.products);
      setPaymentOrders(orderData.orders);
      setMonetizationItems(monetizationData.items);
      setRevenueMethods(revenueData.methods);
      setProviderConfigs(providerConfigData.sources);
      setFeatureAccessRules(featureAccessData.rules);
      if (!featureProductId && productData.products.some((product) => product.active === 1)) setFeatureProductId(productData.products.find((product) => product.active === 1)!.id);
      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => { void loadAdmin(); }, []);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    try {
      await api("/api/admin/login", { method: "POST", body: JSON.stringify({ password }) });
      setPassword("");
      await loadAdmin();
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "登录失败");
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      setConfig(await api<AccessConfig>("/api/admin/config", { method: "PUT", body: JSON.stringify(config) }));
      const auditData = await api<{ records: AuditRecord[] }>("/api/admin/audit");
      setAudits(auditData.records);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "保存失败");
    } finally {
      setSaving(false);
    }
  };

  const togglePaymentMethod = async (method: PaymentMethod) => {
    setError("");
    try {
      const result = await api<{ enabled: boolean }>(`/api/admin/payment-methods/${method.id}`, { method: "PUT", body: JSON.stringify({ enabled: !method.enabled }) });
      setPaymentMethods((current) => current.map((item) => item.id === method.id ? { ...item, enabled: result.enabled } : item));
    } catch (toggleError) {
      setError(toggleError instanceof Error ? toggleError.message : "支付方式状态更新失败");
    }
  };

  const addPaymentProduct = async (event: FormEvent) => {
    event.preventDefault();
    setPaymentSaving(true);
    setError("");
    try {
      const result = await api<{ product: PaymentProduct }>("/api/admin/payment-products", { method: "POST", body: JSON.stringify({ name: newProductName, description: newProductDescription, currency: "CNY", amount: newProductPrice, active: false }) });
      setPaymentProducts((current) => [result.product, ...current]);
      setNewProductName("");
      setNewProductDescription("");
      setNewProductPrice("");
    } catch (productError) {
      setError(productError instanceof Error ? productError.message : "收费项目创建失败");
    } finally {
      setPaymentSaving(false);
    }
  };

  const togglePaymentProduct = async (product: PaymentProduct) => {
    setError("");
    try {
      const result = await api<{ product: PaymentProduct }>("/api/admin/payment-products", { method: "PUT", body: JSON.stringify({ id: product.id, name: product.name, description: product.description, currency: product.currency, amount: (product.amount_minor / 100).toFixed(2), active: !product.active }) });
      setPaymentProducts((current) => current.map((item) => item.id === product.id ? result.product : item));
    } catch (productError) {
      setError(productError instanceof Error ? productError.message : "收费项目状态更新失败");
    }
  };

  const saveFeatureAccessRule = async (event: FormEvent) => {
    event.preventDefault();
    setFeatureSaving(true);
    setError("");
    try {
      await api("/api/admin/feature-access", { method: "POST", body: JSON.stringify({ pageId: featurePageId, scope: featureScope, componentSelector: featureScope === "component" ? featureSelector : "", label: featureLabel || tools.find((tool) => tool.id === featurePageId)?.title || featurePageId, paid: featurePaid, productId: featurePaid ? featureProductId : null }) });
      const result = await api<{ rules: FeatureAccessRule[] }>("/api/admin/feature-access");
      setFeatureAccessRules(result.rules);
    } catch (ruleError) {
      setError(ruleError instanceof Error ? ruleError.message : "功能收费规则保存失败");
    } finally {
      setFeatureSaving(false);
    }
  };

  const deleteFeatureAccessRule = async (rule: FeatureAccessRule) => {
    setError("");
    try {
      await api(`/api/admin/feature-access/${rule.id}`, { method: "DELETE" });
      setFeatureAccessRules((current) => current.filter((item) => item.id !== rule.id));
    } catch (ruleError) {
      setError(ruleError instanceof Error ? ruleError.message : "功能收费规则删除失败");
    }
  };

  const editFeatureAccessRule = (rule: FeatureAccessRule) => {
    setFeaturePageId(rule.page_id);
    setFeatureScope(rule.scope);
    setFeatureSelector(rule.component_selector || COMPONENT_OPTIONS[0][0]);
    setFeatureLabel(rule.label);
    setFeaturePaid(rule.paid === 1);
    setFeatureProductId(rule.product_id || "");
    document.querySelector(".feature-access-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const configurePageAccess = (tool: Tool) => {
    setFeaturePageId(tool.id);
    setFeatureScope("page");
    setFeatureLabel(tool.title);
    setFeaturePaid(false);
    setFeatureProductId("");
    document.querySelector(".feature-access-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const addMonetizationItem = async (event: FormEvent) => {
    event.preventDefault();
    setMonetizationSaving(true);
    setError("");
    try {
      const previous = monetizationItems.find((item) => item.id === editingRevenueId);
      const result = await api<{ item: MonetizationItem }>("/api/admin/monetization-items", {
        method: editingRevenueId ? "PUT" : "POST",
        body: JSON.stringify({ ...(editingRevenueId ? { id: editingRevenueId } : {}), kind: newRevenueKind, provider: newRevenueProvider, title: newRevenueTitle, description: newRevenueDescription, targetUrl: newRevenueUrl, imageUrl: newRevenueImage, placement: newRevenuePlacement, disclosure: newRevenueDisclosure, incomeAmount: newRevenueIncome, active: previous?.active === 1 }),
      });
      if (editingRevenueId) setMonetizationItems((current) => current.map((item) => item.id === editingRevenueId ? { ...result.item, click_count: item.click_count } : item));
      else setMonetizationItems((current) => [{ ...result.item, click_count: 0 }, ...current]);
      const revenueData = await api<{ methods: RevenueMethod[] }>("/api/admin/revenue-methods");
      setRevenueMethods(revenueData.methods);
      setEditingRevenueId("");
      setNewRevenueProvider(""); setNewRevenueTitle(""); setNewRevenueDescription(""); setNewRevenueUrl(""); setNewRevenueImage(""); setNewRevenueIncome("");
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : "推广内容保存失败");
    } finally {
      setMonetizationSaving(false);
    }
  };

  const editMonetizationItem = (item: MonetizationItem) => {
    setEditingRevenueId(item.id);
    setNewRevenueKind(item.kind);
    setNewRevenueProvider(item.provider);
    setNewRevenueTitle(item.title);
    setNewRevenueDescription(item.description);
    setNewRevenueUrl(item.target_url);
    setNewRevenueImage(item.image_url);
    setNewRevenuePlacement(item.placement);
    setNewRevenueDisclosure(item.disclosure);
    setNewRevenueIncome(item.income_minor === null ? "" : (item.income_minor / 100).toFixed(2));
    document.querySelector(".monetization-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const toggleMonetizationItem = async (item: MonetizationItem) => {
    setError("");
    try {
      const result = await api<{ active: boolean }>(`/api/admin/monetization-items/${item.id}`, { method: "PATCH", body: JSON.stringify({ active: !item.active }) });
      setMonetizationItems((current) => current.map((row) => row.id === item.id ? { ...row, active: result.active ? 1 : 0 } : row));
      setRevenueMethods((current) => current.map((row) => row.id === `partner:${item.id}` ? { ...row, active: result.active } : row));
    } catch (itemError) {
      setError(itemError instanceof Error ? itemError.message : "推广内容状态更新失败");
    }
  };

  const beginProviderConfiguration = (source: MonetizationConfigSource) => {
    setSelectedProviderId(source.id);
    setProviderConfigJson(""); setAlipayAppId(""); setAlipayPrivateKey(""); setAlipayPublicKey("");
    if (source.custom) {
      setCustomProviderName(source.name);
      setCustomProviderCategory(source.category);
      setCustomProviderRevenueType(source.revenueType);
    }
    document.querySelector(".provider-config-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const beginNewProviderConfiguration = () => {
    setSelectedProviderId("new_custom");
    setProviderConfigJson(""); setAlipayAppId(""); setAlipayPrivateKey(""); setAlipayPublicKey("");
    setCustomProviderName(""); setCustomProviderCategory("其他"); setCustomProviderRevenueType("third_party");
    document.querySelector(".provider-config-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const refreshProviderConfigs = async () => {
    const [configData, methodData] = await Promise.all([
      api<{ sources: MonetizationConfigSource[] }>("/api/admin/monetization-configs"),
      api<{ methods: PaymentMethod[] }>("/api/admin/payment-methods"),
    ]);
    setProviderConfigs(configData.sources);
    setPaymentMethods(methodData.methods);
  };

  const saveProviderConfiguration = async (event: FormEvent) => {
    event.preventDefault();
    setConfigSaving(true); setError("");
    try {
      const configJson = selectedProviderId === "alipay"
        ? JSON.stringify({ app_id: alipayAppId.trim(), private_key: alipayPrivateKey, public_key: alipayPublicKey })
        : providerConfigJson;
      if (selectedProviderId === "new_custom") {
        await api("/api/admin/monetization-configs", { method: "POST", body: JSON.stringify({ name: customProviderName, category: customProviderCategory, revenueType: customProviderRevenueType, configJson }) });
      } else {
        await api(`/api/admin/monetization-configs/${selectedProviderId}`, { method: "PUT", body: JSON.stringify({ configJson }) });
      }
      await refreshProviderConfigs();
      setProviderConfigJson(""); setAlipayAppId(""); setAlipayPrivateKey(""); setAlipayPublicKey("");
      if (selectedProviderId === "new_custom") setSelectedProviderId("alipay");
    } catch (configError) {
      setError(configError instanceof Error ? configError.message : "变现配置保存失败");
    } finally {
      setConfigSaving(false);
    }
  };

  const clearProviderConfiguration = async (source: MonetizationConfigSource) => {
    if (!window.confirm(`确定清除“${source.name}”保存的配置吗？`)) return;
    setError("");
    try {
      await api(`/api/admin/monetization-configs/${source.id}`, { method: "DELETE" });
      await refreshProviderConfigs();
      if (selectedProviderId === source.id) beginProviderConfiguration(source);
    } catch (configError) {
      setError(configError instanceof Error ? configError.message : "变现配置清除失败");
    }
  };

  const logout = async () => {
    await api("/api/admin/logout", { method: "POST", body: "{}" });
    setAuthenticated(false);
  };

  const filterCountries = (query: string, selected: string[]) => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return selected;
    return COUNTRY_CODES.filter((code) => code.toLowerCase().includes(normalized) || countryName(code).toLowerCase().includes(normalized));
  };
  const effectiveAllowedCountries = useMemo(() => COUNTRY_CODES.filter((code) => {
    if (config.blockedCountries.includes(code)) return false;
    if (config.allowedCountries.includes(code)) return true;
    return config.mode !== "allow_list";
  }), [config.mode, config.allowedCountries, config.blockedCountries]);
  const effectiveBlockedCountries = useMemo(() => COUNTRY_CODES.filter((code) => !effectiveAllowedCountries.includes(code)), [effectiveAllowedCountries]);
  const allowCountries = useMemo(() => filterCountries(allowQuery, effectiveAllowedCountries), [allowQuery, effectiveAllowedCountries]);
  const blockCountries = useMemo(() => filterCountries(blockQuery, effectiveBlockedCountries), [blockQuery, effectiveBlockedCountries]);

  const assignCountry = (code: string, target: "allowedCountries" | "blockedCountries", checked: boolean) => {
    setConfig((current) => {
      const other = target === "allowedCountries" ? "blockedCountries" : "allowedCountries";
      const destination = checked ? target : other;
      const source = destination === target ? other : target;
      return {
        ...current,
        [destination]: [...new Set([...current[destination], code])].sort(),
        [source]: current[source].filter((item) => item !== code),
      };
    });
  };

  const moveAllCountries = (source: "allowedCountries" | "blockedCountries") => {
    const target = source === "allowedCountries" ? "blockedCountries" : "allowedCountries";
    setConfig((current) => ({
      ...current,
      allowedCountries: target === "allowedCountries" ? COUNTRY_CODES : [],
      blockedCountries: target === "blockedCountries" ? COUNTRY_CODES : [],
    }));
  };

  if (checking) return <main className="admin-login"><div className="loader" /><p>正在验证后台会话…</p></main>;

  if (!authenticated) {
    return (
      <main className="admin-login">
        <button className="back-link" onClick={() => navigate("/")}>← 返回网站</button>
        <form className="login-card" onSubmit={login}>
          <div className="brand-mark">明</div>
          <h1>访问控制后台</h1>
          <p className="lede">清单只显示已选国家。搜索后可勾选国家；勾选后会加入清单，并从另一清单移除。</p>
          <label className="field"><span>管理员密码</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
          {error && <p className="error-message">{error}</p>}
          <button className="primary-button" type="submit" disabled={!password}>安全登录</button>
        </form>
      </main>
    );
  }

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div><h1>管理后台</h1></div>
        <div className="button-row"><button onClick={() => navigate("/")}>查看前台</button><button onClick={logout}>退出登录</button></div>
      </header>

      <div className="admin-layout">
      <nav className="admin-sidebar" aria-label="后台板块">
        <a href="#allowed-countries">允许访问的国家</a>
        <a href="#blocked-countries">禁止访问的国家</a>
        <a href="#monetization-config">变现配置</a>
        <a href="#feature-access">页面与组件收费</a>
        <a href="#revenue-methods">变现方式</a>
        <a href="#payment-methods">支付方式</a>
        <a href="#promotion-items">广告与推广</a>
        <a href="#page-visits">页面访问记录</a>
        <a href="#admin-audit">最近操作</a>
      </nav>
      <div className="admin-main">
      {error && <p className="error-message">{error}</p>}

      <CountryModule id="allowed-countries" title="允许访问的国家" bulkActionLabel="全部禁止" countries={allowCountries} selected={effectiveAllowedCountries} query={allowQuery} onQuery={setAllowQuery} onToggle={(code, checked) => assignCountry(code, "allowedCountries", checked)} onBulkAction={() => moveAllCountries("allowedCountries")} />
      <CountryModule id="blocked-countries" title="禁止访问的国家" bulkActionLabel="全部允许" countries={blockCountries} selected={effectiveBlockedCountries} query={blockQuery} onQuery={setBlockQuery} onToggle={(code, checked) => assignCountry(code, "blockedCountries", checked)} onBulkAction={() => moveAllCountries("blockedCountries")} />

      <div className="save-bar"><div><b>{effectiveAllowedCountries.length} 个允许 · {effectiveBlockedCountries.length} 个禁止</b></div><button className="primary-button" onClick={save} disabled={saving}>{saving ? "正在保存…" : "保存并发布策略"}</button></div>

      <section className="admin-section provider-config-section" id="monetization-config">
        <div className="section-heading"><div><h2>变现配置</h2></div><span>{providerConfigs.filter((source) => source.configured).length} 个已配置 · {providerConfigs.length} 个渠道</span></div>
        <form className="provider-config-form" onSubmit={(event) => void saveProviderConfiguration(event)}>
          <label className="field"><span>选择渠道</span><select value={selectedProviderId} onChange={(event) => { const id = event.target.value; if (id === "new_custom") beginNewProviderConfiguration(); else { const source = providerConfigs.find((item) => item.id === id); if (source) beginProviderConfiguration(source); else setSelectedProviderId(id); } }}><option value="new_custom">新增自定义渠道</option>{providerConfigs.map((source) => <option value={source.id} key={source.id}>{source.name}（{source.category}）</option>)}</select></label>
          {selectedProviderId === "new_custom" && <>
            <label className="field"><span>渠道名称</span><input required maxLength={100} value={customProviderName} onChange={(event) => setCustomProviderName(event.target.value)} placeholder="例如：其他广告平台" /></label>
            <label className="field"><span>类别</span><select value={customProviderCategory} onChange={(event) => setCustomProviderCategory(event.target.value)}><option>支付</option><option>广告</option><option>电商 CPS</option><option>品牌合作</option><option>推荐返佣</option><option>打赏</option><option>其他</option></select></label>
            <label className="field"><span>变现类型</span><select value={customProviderRevenueType} onChange={(event) => setCustomProviderRevenueType(event.target.value as "direct" | "third_party")}><option value="direct">用户直接支付</option><option value="third_party">第三方支付</option></select></label>
          </>}
          {selectedProviderId === "alipay" ? <>
            <label className="field"><span>支付宝 App ID</span><input data-private="true" autoComplete="off" value={alipayAppId} onChange={(event) => setAlipayAppId(event.target.value)} placeholder="输入商户 App ID" /></label>
            <label className="field provider-config-wide"><span>应用 RSA 私钥</span><textarea data-private="true" autoComplete="off" value={alipayPrivateKey} onChange={(event) => setAlipayPrivateKey(event.target.value)} placeholder="-----BEGIN PRIVATE KEY-----" rows={4} /></label>
            <label className="field provider-config-wide"><span>支付宝 RSA 公钥</span><textarea data-private="true" autoComplete="off" value={alipayPublicKey} onChange={(event) => setAlipayPublicKey(event.target.value)} placeholder="-----BEGIN PUBLIC KEY-----" rows={4} /></label>
          </> : selectedProviderId !== "new_custom" && !selectedProviderId.startsWith("custom:") && <label className="field provider-config-wide"><span>渠道配置（JSON）</span><textarea data-private="true" autoComplete="off" value={providerConfigJson} onChange={(event) => setProviderConfigJson(event.target.value)} placeholder={selectedProviderId === "adsense" ? '{\n  "publisher_id": "ca-pub-...",\n  "ad_slots": []\n}' : selectedProviderId.includes("_cps") || selectedProviderId === "amazon_associates" ? '{\n  "account_id": "",\n  "app_key": "",\n  "app_secret": "",\n  "site_id": ""\n}' : '{\n  "merchant_id": "",\n  "api_key": "",\n  "private_key": "",\n  "callback_url": ""\n}'} rows={6} /></label>}
          {(selectedProviderId === "new_custom" || selectedProviderId.startsWith("custom:")) && <label className="field provider-config-wide"><span>渠道配置（JSON）</span><textarea data-private="true" autoComplete="off" value={providerConfigJson} onChange={(event) => setProviderConfigJson(event.target.value)} placeholder={'{\n  "account_id": "",\n  "app_key": "",\n  "app_secret": "",\n  "callback_url": ""\n}'} rows={6} /></label>}
          <div className="provider-config-actions"><button className="primary-button" type="submit" disabled={configSaving}>{configSaving ? "正在加密保存…" : "保存渠道配置"}</button><span>密钥会加密保存，后台不回显。只有代码已接入的渠道可以实际启用。</span></div>
        </form>
        <div className="payment-table-wrap"><table className="payment-table"><thead><tr><th>名称</th><th>类型</th><th>代码状态</th><th>配置状态</th><th>更新时间</th><th>操作</th></tr></thead><tbody>
          {providerConfigs.map((source) => <tr key={source.id}><td><b>{source.name}</b></td><td>{source.revenueType === "direct" ? "用户直接支付" : "第三方支付"}</td><td>{source.integration === "ready" ? "已接入" : "待接入代码"}</td><td>{source.configured ? "已加密保存" : "未配置"}</td><td>{source.updatedAt || "—"}</td><td><div className="button-row"><button onClick={() => beginProviderConfiguration(source)}>{source.configured ? "重新配置" : "配置"}</button>{source.configured && <button onClick={() => void clearProviderConfiguration(source)}>清除</button>}</div></td></tr>)}
        </tbody></table></div>
      </section>

      <section className="admin-section feature-access-admin-section" id="feature-access">
        <div className="section-heading"><div><h2>页面与组件收费</h2></div><span>{featureAccessRules.length} 条访问规则</span></div>
        <form className="feature-access-form" onSubmit={(event) => void saveFeatureAccessRule(event)}>
          <label className="field"><span>页面</span><select value={featurePageId} onChange={(event) => { const id = event.target.value; setFeaturePageId(id); setFeatureLabel(tools.find((tool) => tool.id === id)?.title || id); }}>
            {tools.map((tool) => <option key={tool.id} value={tool.id}>{tool.title}（{tool.categoryName}）</option>)}
          </select></label>
          <label className="field"><span>控制范围</span><select value={featureScope} onChange={(event) => setFeatureScope(event.target.value as "page" | "component")}><option value="page">整个页面</option><option value="component">页面内组件</option></select></label>
          {featureScope === "component" && <label className="field"><span>组件选择器</span><input list="feature-component-selectors" value={featureSelector} onChange={(event) => setFeatureSelector(event.target.value)} placeholder="例如 .result-box 或 .assessment-row:nth-of-type(2)" /><datalist id="feature-component-selectors">{COMPONENT_OPTIONS.map(([selector, name]) => <option key={selector} value={selector}>{name}</option>)}</datalist></label>}
          <label className="field"><span>功能名称</span><input required maxLength={120} value={featureLabel} onChange={(event) => setFeatureLabel(event.target.value)} placeholder="例如：测评结果" /></label>
          <label className="field"><span>使用方式</span><select value={featurePaid ? "paid" : "free"} onChange={(event) => setFeaturePaid(event.target.value === "paid")}><option value="free">免费使用</option><option value="paid">付费使用</option></select></label>
          {featurePaid && <label className="field"><span>关联收费项目</span><select required value={featureProductId} onChange={(event) => setFeatureProductId(event.target.value)}><option value="">选择已上架的收费项目</option>{paymentProducts.filter((product) => product.active === 1).map((product) => <option key={product.id} value={product.id}>{product.name} · ¥{(product.amount_minor / 100).toFixed(2)}</option>)}</select></label>}
          <div className="feature-access-form-actions"><button className="primary-button" type="submit" disabled={featureSaving || (featurePaid && !featureProductId)}>{featureSaving ? "正在保存…" : "保存访问规则"}</button><span>组件可用 CSS 选择器定位；重复保存同一页面和组件会更新原规则。</span></div>
        </form>
        <label className="field feature-access-search"><span>搜索页面或组件规则</span><input type="search" value={featureSearch} onChange={(event) => setFeatureSearch(event.target.value)} placeholder="输入工具名称、组件名称或选择器" /></label>
        <div className="payment-table-wrap"><table className="payment-table"><thead><tr><th>页面</th><th>范围</th><th>功能</th><th>使用方式</th><th>收费项目</th><th>操作</th></tr></thead><tbody>
          {tools.filter((tool) => `${tool.title} ${tool.subtitle} ${tool.categoryName}`.toLowerCase().includes(featureSearch.trim().toLowerCase())).map((tool) => {
            const rule = featureAccessRules.find((item) => item.page_id === tool.id && item.scope === "page");
            return <tr key={`page:${tool.id}`}><td>{tool.title}</td><td>整个页面</td><td><b>{tool.title}</b></td><td>{rule?.paid ? "付费使用" : "免费使用"}</td><td>{rule?.paid ? `${rule.product_name || "收费项目已删除"}${rule.amount_minor === null ? "" : ` · ¥${(rule.amount_minor / 100).toFixed(2)}`}` : "—"}</td><td>{rule ? <div className="button-row"><button onClick={() => editFeatureAccessRule(rule)}>编辑</button><button onClick={() => void deleteFeatureAccessRule(rule)}>恢复免费</button></div> : <button onClick={() => configurePageAccess(tool)}>配置</button>}</td></tr>;
          })}
          {featureAccessRules.filter((rule) => rule.scope === "component" && `${tools.find((tool) => tool.id === rule.page_id)?.title || rule.page_id} ${rule.label} ${rule.component_selector}`.toLowerCase().includes(featureSearch.trim().toLowerCase())).map((rule) => <tr key={rule.id}><td>{tools.find((tool) => tool.id === rule.page_id)?.title || rule.page_id}</td><td>组件 · {rule.component_selector}</td><td><b>{rule.label}</b></td><td>{rule.paid ? "付费使用" : "免费使用"}</td><td>{rule.paid ? `${rule.product_name || "收费项目已删除"}${rule.amount_minor === null ? "" : ` · ¥${(rule.amount_minor / 100).toFixed(2)}`}` : "—"}</td><td><div className="button-row"><button onClick={() => editFeatureAccessRule(rule)}>编辑</button><button onClick={() => void deleteFeatureAccessRule(rule)}>删除规则</button></div></td></tr>)}
        </tbody></table></div>
      </section>

      <section className="admin-section revenue-methods-section" id="revenue-methods">
        <div className="section-heading"><div><h2>变现方式</h2></div><span>{revenueMethods.length} 种</span></div>
        <div className="payment-table-wrap"><table className="payment-table"><thead><tr><th>名称</th><th>类型</th><th>收入</th></tr></thead><tbody>
          {revenueMethods.length === 0 && <tr><td colSpan={3} className="empty">暂无变现方式</td></tr>}
          {revenueMethods.map((method) => <tr key={method.id}><td><b>{method.name}</b>{method.type === "第三方支付" && <small>{method.active ? "上架中" : "已下架"}</small>}</td><td>{method.type}</td><td>{method.incomeMinor === null ? "待录入" : `¥${(method.incomeMinor / 100).toFixed(2)}`}<small>{method.incomeStatus}{method.paidOrders !== undefined ? ` · ${method.paidOrders} 笔已支付` : ""}</small></td></tr>)}
        </tbody></table></div>
      </section>

      <section className="admin-section payment-admin-section" id="payment-methods">
        <div className="section-heading"><div><h2>支付方式</h2></div><span>{paymentMethods.filter((method) => method.integration === "ready").length} 个已接入 · {paymentMethods.filter((method) => method.integration !== "ready").length} 个未接入</span></div>
        <div className="payment-method-list">
          {paymentMethods.map((method) => <article className="payment-method-row" key={method.id}>
            <div className="payment-method-title"><b>{method.name}</b><small>{method.group} · {method.coverage}</small></div>
            <div className="payment-method-details"><span className={`payment-status ${method.integration === "ready" ? "ready" : "pending"}`}>{method.status}</span><small>{method.credentialStatus}</small><small>主体：{method.entities}</small><small>{method.requirements}</small></div>
            <button className={method.enabled ? "payment-publish-button published" : "payment-publish-button"} disabled={method.integration !== "ready" || !method.configured} title={!method.configured && method.integration === "ready" ? "配置商户凭证后即可上架" : undefined} onClick={() => void togglePaymentMethod(method)}>{method.enabled ? "下架" : method.configured ? "上架" : "待配置"}</button>
          </article>)}
        </div>

        <div className="payment-subsection-heading"><h3>收费项目</h3><span>创建项目后可在后续功能中绑定收银入口</span></div>
        <form className="payment-product-form" onSubmit={(event) => void addPaymentProduct(event)}>
          <label className="field"><span>项目名称</span><input required maxLength={100} value={newProductName} onChange={(event) => setNewProductName(event.target.value)} placeholder="例如：高级功能" /></label>
          <label className="field"><span>价格（人民币）</span><input required inputMode="decimal" pattern="[0-9]+(\.[0-9]{1,2})?" value={newProductPrice} onChange={(event) => setNewProductPrice(event.target.value)} placeholder="0.00" /></label>
          <label className="field payment-product-description"><span>说明</span><input maxLength={500} value={newProductDescription} onChange={(event) => setNewProductDescription(event.target.value)} placeholder="项目说明" /></label>
          <button className="primary-button" type="submit" disabled={paymentSaving}>{paymentSaving ? "正在保存…" : "新增项目"}</button>
        </form>
        {paymentProducts.length > 0 && <div className="payment-table-wrap"><table className="payment-table"><thead><tr><th>项目</th><th>金额</th><th>状态</th><th>操作</th></tr></thead><tbody>{paymentProducts.map((product) => <tr key={product.id}><td><b>{product.name}</b><small>{product.description || "—"}</small></td><td>¥{(product.amount_minor / 100).toFixed(2)}</td><td>{product.active ? "销售中" : "已下架"}</td><td><button onClick={() => void togglePaymentProduct(product)}>{product.active ? "下架" : "上架"}</button></td></tr>)}</tbody></table></div>}

        <div className="payment-subsection-heading"><h3>最近支付订单</h3><span>{paymentOrders.length} 条</span></div>
        {paymentOrders.length === 0 ? <p className="empty">暂无支付订单</p> : <div className="payment-table-wrap"><table className="payment-table"><thead><tr><th>订单</th><th>项目</th><th>金额</th><th>状态</th><th>用户名</th><th>创建时间</th></tr></thead><tbody>{paymentOrders.map((order) => <tr key={order.id}><td><code>{order.id.slice(0, 8)}</code></td><td>{order.product_name}</td><td>¥{(order.amount_minor / 100).toFixed(2)}</td><td>{paymentStatusName(order.status)}</td><td>{order.username || "未登录"}</td><td>{new Date(`${order.created_at.replace(" ", "T")}Z`).toLocaleString("zh-CN")}</td></tr>)}</tbody></table></div>}
      </section>

      <section className="admin-section monetization-admin-section" id="promotion-items">
        <div className="section-heading"><div><h2>广告与推广</h2></div><span>{monetizationItems.filter((item) => item.active).length} 个上架 · {monetizationItems.length} 个项目 · {monetizationItems.reduce((sum, item) => sum + Number(item.click_count || 0), 0)} 次点击</span></div>
        <form className="monetization-form" onSubmit={(event) => void addMonetizationItem(event)}>
          <label className="field"><span>收益方式</span><select value={newRevenueKind} onChange={(event) => { const kind = event.target.value as MonetizationItem["kind"]; setNewRevenueKind(kind); if (kind === "ad") setNewRevenueDisclosure("广告"); else if (kind === "cps") setNewRevenueDisclosure("推广链接，成交后可能获得佣金。"); else if (kind === "sponsor") setNewRevenueDisclosure("品牌合作内容"); else if (kind === "referral") setNewRevenueDisclosure("推荐链接，可能获得奖励。"); else setNewRevenueDisclosure("自愿支持，不影响工具使用。"); }}><option value="ad">广告</option><option value="cps">电商 CPS</option><option value="sponsor">品牌合作</option><option value="referral">推荐返佣</option><option value="donation">打赏支持</option></select></label>
          <label className="field"><span>平台 / 联盟</span><input maxLength={80} value={newRevenueProvider} onChange={(event) => setNewRevenueProvider(event.target.value)} placeholder="如：京东联盟、淘宝联盟、AdSense" /></label>
          <label className="field"><span>展示标题</span><input required maxLength={120} value={newRevenueTitle} onChange={(event) => setNewRevenueTitle(event.target.value)} placeholder="推荐内容标题" /></label>
          <label className="field"><span>展示位置</span><select value={newRevenuePlacement} onChange={(event) => setNewRevenuePlacement(event.target.value as MonetizationItem["placement"])}><option value="home">首页</option><option value="tool">工具页</option><option value="both">首页和工具页</option></select></label>
          <label className="field monetization-wide"><span>推广链接（HTTPS）</span><input required type="url" value={newRevenueUrl} onChange={(event) => setNewRevenueUrl(event.target.value)} placeholder="填写联盟生成的推广链接" /></label>
          <label className="field monetization-wide"><span>图片链接（可选）</span><input type="url" value={newRevenueImage} onChange={(event) => setNewRevenueImage(event.target.value)} placeholder="HTTPS 图片地址" /></label>
          <label className="field monetization-wide"><span>描述</span><textarea maxLength={500} value={newRevenueDescription} onChange={(event) => setNewRevenueDescription(event.target.value)} placeholder="为用户说明推荐内容" rows={2} /></label>
          <label className="field"><span>收益披露</span><input required maxLength={200} value={newRevenueDisclosure} onChange={(event) => setNewRevenueDisclosure(event.target.value)} /></label>
          <label className="field"><span>已确认收入（人民币）</span><input inputMode="decimal" pattern="[0-9]+(\.[0-9]{1,2})?" value={newRevenueIncome} onChange={(event) => setNewRevenueIncome(event.target.value)} placeholder="联盟结算后录入" /></label>
          <div className="monetization-form-actions"><button className="primary-button" type="submit" disabled={monetizationSaving}>{monetizationSaving ? "正在保存…" : editingRevenueId ? "保存修改" : "新增推广项目"}</button>{editingRevenueId && <button type="button" onClick={() => { setEditingRevenueId(""); setNewRevenueProvider(""); setNewRevenueTitle(""); setNewRevenueDescription(""); setNewRevenueUrl(""); setNewRevenueImage(""); setNewRevenueIncome(""); }}>取消编辑</button>}</div>
        </form>
        <div className="payment-table-wrap"><table className="payment-table monetization-table"><thead><tr><th>方式 / 平台</th><th>内容</th><th>位置</th><th>点击</th><th>状态</th><th>操作</th></tr></thead><tbody>
          {monetizationItems.length === 0 && <tr><td colSpan={6} className="empty">暂无推广项目</td></tr>}
          {monetizationItems.map((item) => <tr key={item.id}><td>{({ ad: "广告", cps: "电商 CPS", sponsor: "品牌合作", referral: "推荐返佣", donation: "打赏支持" } as Record<MonetizationItem["kind"], string>)[item.kind]}<small>{item.provider || "未填写平台"}</small></td><td><b>{item.title}</b><small>{item.disclosure}</small></td><td>{{ home: "首页", tool: "工具页", both: "首页和工具页" }[item.placement]}</td><td>{item.click_count || 0}</td><td>{item.active ? "上架中" : "已下架"}</td><td><div className="button-row"><button onClick={() => editMonetizationItem(item)}>编辑</button><button onClick={() => void toggleMonetizationItem(item)}>{item.active ? "下架" : "上架"}</button></div></td></tr>)}
        </tbody></table></div>
      </section>

      <section className="admin-section audit-section" id="page-visits">
        <div className="section-heading"><div><h2>页面访问记录</h2></div><span>共 {visitsTotal} 次访问</span></div>
        <div ref={topVisitsScrollbar} className="visits-extra-scrollbar visits-extra-scrollbar-top" role="region" aria-label="横向滚动访问记录表格" aria-controls="page-visits-table" tabIndex={0}><div ref={topVisitsScrollbarSpace} /></div>
        <div ref={visitsTableWrap} className="visits-table-wrap"><table id="page-visits-table" className="visits-table"><thead><tr><th>北京时间</th><th>访客当地时间</th><th>访客时区</th><th>用户名</th><th>页面</th><th>访问端</th><th>停留时间</th><th>国家/地区</th><th>IP 地址</th><th>地理位置详情</th><th>设备类型</th><th>制造商</th><th>设备型号</th><th>操作系统</th><th>系统版本</th><th>浏览器</th><th>浏览器版本</th><th>CPU 架构</th><th>位数</th><th>逻辑处理器</th><th>内存</th><th>屏幕分辨率</th><th>MAC 地址</th><th>IMEI</th><th>设备序列号</th><th>浏览器访客 ID</th><th>User-Agent</th></tr></thead><tbody>
          {visits.length === 0 && <tr><td colSpan={27} className="empty">暂无访问记录</td></tr>}
          {visits.map((visit) => <tr key={visit.id} className="visit-row" tabIndex={0} onClick={() => void openVisitDetails(visit)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void openVisitDetails(visit); } }} title="查看组件操作明细"><td>{formatVisitTime(visit.visited_at, "Asia/Shanghai")}</td><td>{formatVisitTime(visit.visited_at, visit.visitor_timezone)}</td><td>{visit.visitor_timezone || "未知"}</td><td>{visit.username || "未登录"}</td><td><code>{visit.page_path}</code></td><td>{visitClientType(visit.page_path)}</td><td>{!visit.visit_token ? "—" : visit.ended_at ? formatDuration(visit.duration_seconds) : "统计中"}</td><td>{countryName(visit.country)}（{visit.country}）</td><td>{visit.ip_address}</td><td>{visit.location_detail || "未知"}</td><td>{visit.device_type || "未知"}</td><td>{visit.manufacturer}</td><td>{visit.device_model}</td><td>{visit.operating_system || "未知"}</td><td>{visit.platform_version}</td><td>{visit.browser || "未知"}</td><td>{visit.browser_version}</td><td>{visit.cpu_architecture}</td><td>{visit.cpu_bitness}</td><td>{visit.cpu_cores}</td><td>{visit.device_memory_gb}</td><td>{visit.screen_resolution}</td><td>网页不可读取</td><td>网页不可读取</td><td>网页不可读取</td><td><code>{visit.browser_id || "不可用"}</code><small>本网站随机编号，不是硬件 ID</small></td><td className="user-agent-cell">{visit.user_agent || "未知"}</td></tr>)}
        </tbody></table></div>
        <div ref={bottomVisitsScrollbar} hidden className="visits-extra-scrollbar visits-extra-scrollbar-bottom" role="region" aria-label="屏幕底部横向滚动访问记录" aria-controls="page-visits-table" tabIndex={0}><div ref={bottomVisitsScrollbarSpace} /></div>
        <div className="visit-pagination"><span>{visitsTotal ? `${visitsOffset + 1}–${Math.min(visitsOffset + visits.length, visitsTotal)} / ${visitsTotal}` : "0 条记录"}</span><div className="button-row"><button disabled={visitsOffset === 0} onClick={() => void loadVisits(Math.max(0, visitsOffset - 50))}>上一页</button><button disabled={visitsOffset + 50 >= visitsTotal} onClick={() => void loadVisits(visitsOffset + 50)}>下一页</button></div></div>
      </section>

      <section className="admin-section audit-section" id="admin-audit">
        <div className="section-heading"><div><h2>最近操作</h2></div></div>
        <div className="audit-list">
          {audits.length === 0 && <p className="empty">暂无操作记录</p>}
          {audits.map((record) => <div key={record.id}><span className="audit-icon">↗</span><div><b>{record.action}</b><small>{record.country || "未知地区"} · {new Date(`${record.created_at}Z`).toLocaleString("zh-CN")}</small></div></div>)}
        </div>
      </section>
      {selectedVisit && <div className="visit-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectedVisit(null); }}><section className="visit-modal" role="dialog" aria-modal="true" aria-labelledby="visit-details-title">
        <div className="section-heading"><div><h2 id="visit-details-title">页面操作明细</h2><p>{selectedVisit.username || "未登录"} · <code>{selectedVisit.page_path}</code> · {countryName(selectedVisit.country)}（{selectedVisit.country}） · {selectedVisit.ip_address} · {selectedVisit.location_detail} · {selectedVisit.visitor_timezone} · {selectedVisit.device_type} · {selectedVisit.operating_system} · {selectedVisit.browser} · 浏览器访客 ID：{selectedVisit.browser_id}</p></div><button className="visit-modal-close" onClick={() => setSelectedVisit(null)} aria-label="关闭">×</button></div>
        {loadingVisitEvents ? <p className="empty">正在加载操作记录…</p> : visitEvents.length === 0 ? <p className="empty">这次访问暂无组件操作记录</p> : <div className="visit-event-list">{visitEvents.map((event) => <article className="visit-event" key={event.id}><time>{new Date(`${event.occurred_at.replace(" ", "T")}Z`).toLocaleString("zh-CN")}</time><div><b>{event.component}</b><small>{event.component_type} · {event.action}</small>{event.value && <p>{event.value}</p>}</div></article>)}</div>}
      </section></div>}
      </div>
      </div>
    </main>
  );
}

function CountryModule({ id, title, bulkActionLabel, countries, selected, query, onQuery, onToggle, onBulkAction }: { id: string; title: string; bulkActionLabel: string; countries: string[]; selected: string[]; query: string; onQuery: (value: string) => void; onToggle: (code: string, checked: boolean) => void; onBulkAction: () => void }) {
  return <section className="admin-section" id={id}>
    <div className="section-heading"><div><h2>{title}</h2></div><span>{selected.length} 个已选择</span></div>
    <div className="country-toolbar"><input type="search" placeholder="搜索国家名称或两位代码" value={query} onChange={(event) => onQuery(event.target.value)} /><button onClick={() => onQuery("")}>清空</button><button onClick={onBulkAction} disabled={selected.length === 0}>{bulkActionLabel}（{selected.length}）</button></div>
    <div className="country-grid">{countries.map((code) => <label key={code} className={selected.includes(code) ? "country selected" : "country"}><input type="checkbox" checked={selected.includes(code)} onChange={(event) => onToggle(code, event.target.checked)} /><span><b>{countryName(code)}</b><small>{code}</small></span></label>)}</div>
  </section>;
}
